import { formatDuration } from '../../../lib/formatUtils';
import type { WeldDrillDownRow } from '../components/dashboard/drilldown/drillDownTypes';

export interface WeldDurationDisplay {
  /** Production weld time (arc-on), not the clock span. */
  label: string;
  /** Set when start → end is meaningfully longer or shorter than weld time. */
  clockSpanLabel: string | null;
}

const SPAN_MATCH_TOLERANCE_SEC = 90;

function formatClockSpan(startMs: number, endMs: number): string {
  const totalSec = Math.max(0, Math.round((endMs - startMs) / 1000));
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || hours > 0) parts.push(`${minutes}m`);
  if (seconds > 0 && hours === 0) parts.push(`${seconds}s`);
  return parts.length > 0 ? parts.join(' ') : '0s';
}

function minutesToLabel(minutes: number): string {
  return String(formatDuration(minutes));
}

function weldTimeSeconds(row: WeldDrillDownRow): number | null {
  if (row.durationSec != null && Number.isFinite(row.durationSec)) return row.durationSec;
  const prodTime = row.productionRow?.weldTime;
  if (typeof prodTime === 'number' && Number.isFinite(prodTime)) return prodTime * 60;
  if (prodTime != null && prodTime !== '') {
    const match = String(prodTime).match(/(-?\d+\.?\d*)/);
    if (match) {
      const minutes = parseFloat(match[1]);
      if (Number.isFinite(minutes)) return minutes * 60;
    }
  }
  return null;
}

export function weldDurationDisplay(row: WeldDrillDownRow): WeldDurationDisplay {
  const prodTime = row.productionRow?.weldTime;
  let label = '—';
  if (prodTime != null && prodTime !== '') {
    label = minutesToLabel(typeof prodTime === 'number' ? prodTime : Number.NaN);
    if (typeof prodTime !== 'number') {
      label = String(formatDuration(prodTime as string | number));
    }
  } else if (row.durationSec == null) {
    label = '—';
  } else if (row.durationSec < 60) {
    label = `${row.durationSec.toFixed(1)}s`;
  } else {
    label = minutesToLabel(row.durationSec / 60);
  }

  const start = row.startTimeMs;
  const end = row.endTimeMs;
  const arcSec = weldTimeSeconds(row);
  if (start == null || end == null || end < start) {
    return { label, clockSpanLabel: null };
  }
  const spanSec = (end - start) / 1000;
  if (arcSec != null && Math.abs(spanSec - arcSec) <= SPAN_MATCH_TOLERANCE_SEC) {
    return { label, clockSpanLabel: null };
  }
  // Clock-span hint removed from UI — keep null for callers.
  return { label, clockSpanLabel: null };
}
